import { useSyncExternalStore } from 'react';
import { decorateRep, useSalesMeQuery, useSalesRepsQuery } from '@/client/api/sales/sales';
import {
  ACTIVE_CLAIM_STATUSES,
  DEFAULT_SALES_POLICY,
  LOSS_REASONS,
  SALES_GOALS,
  SalesError,
  buildMatchKeys,
  channelNeedsConsent,
  clamp01,
  deriveAcquisitionStage,
  isInPool,
  outreachAsTouch,
  spendBandPart,
  type AcquisitionStage,
  type Availability,
  type ClaimStatus,
  type ClaimWin,
  type ConsentChannel,
  type FitWeights,
  type LossReason,
  type OutreachChannel,
  type OutreachFeedbackOutcome,
  type OutreachFeedbackSentiment,
  type OutreachMode,
  type OutreachStatus,
  type PerformancePeriod,
  type PerformanceSnapshot,
  type ProfileField,
  type SalesClaim,
  type SalesEventType,
  type SalesGoal,
  type SalesOffer,
  type SalesOutreach,
  type SalesPolicy,
  type SalesPurpose,
  type SalesRep,
  type SalesRepRole,
  type SalesSubject,
  type SalesTouch,
  type SubjectKind,
  type SpendBand,
  type TouchChannel,
  type SalesVertical,
} from '@/client/types/sales';
import {
  SALES_CLAIMS,
  SALES_EVENTS,
  SALES_OFFERS,
  SALES_OUTREACHES,
  SALES_PURPOSES,
  SALES_REPS,
  SALES_SUBJECTS,
} from './fixtures';
import {
  MARKETPLACE_HITS,
  hitMatchesSubject,
  marketplaceHitById,
  type MarketplaceHit,
  type MarketplaceQuery,
} from './marketplace-catalog';

const REP_STORAGE_KEY = 'ff-admin.sales.currentRepId.v2';
export const NO_ONE = 'none';

function nextId(prefix: string): string {
  return `${prefix}_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 7)}`;
}

function clone<T>(v: T): T {
  return JSON.parse(JSON.stringify(v)) as T;
}

function fail(code: string, message: string, details: Record<string, unknown> = {}): never {
  throw new SalesError(code, message, details);
}

function readStoredRep(): string {
  try {
    const id = sessionStorage.getItem(REP_STORAGE_KEY);
    if (id) return id;
  } catch {
    /* ignore */
  }
  return NO_ONE;
}

let subjects = SALES_SUBJECTS.map((s) => clone(s));
let claims = SALES_CLAIMS.map((c) => clone(c));
let outreaches = SALES_OUTREACHES.map((o) => clone(o));
let events = SALES_EVENTS.map((e) => clone(e));
let purposes = SALES_PURPOSES.map((p) => clone(p));
let offers = SALES_OFFERS.map((o) => clone(o));
let policy: SalesPolicy = clone(DEFAULT_SALES_POLICY);
let clockOffsetMs = 0;
let currentRepId = typeof window !== 'undefined' ? readStoredRep() : NO_ONE;
// What the viewer just tried to do while acting as no one; non-null opens the
// "who are you acting as?" prompt in SalesPageShell.
let actAsPrompt: string | null = null;

type SalesSnapshot = {
  subjects: typeof subjects;
  claims: typeof claims;
  outreaches: typeof outreaches;
  events: typeof events;
  purposes: typeof purposes;
  offers: typeof offers;
  policy: SalesPolicy;
  clockOffsetMs: number;
  currentRepId: string;
  actAsPrompt: string | null;
};

let snapshot: SalesSnapshot = {
  subjects,
  claims,
  outreaches,
  events,
  purposes,
  offers,
  policy,
  clockOffsetMs,
  currentRepId,
  actAsPrompt,
};

const listeners = new Set<() => void>();
const emit = () => {
  snapshot = {
    subjects,
    claims,
    outreaches,
    events,
    purposes,
    offers,
    policy,
    clockOffsetMs,
    currentRepId,
    actAsPrompt,
  };
  listeners.forEach((l) => l());
};
const subscribe = (l: () => void) => (listeners.add(l), () => listeners.delete(l));
const getSnapshot = () => snapshot;

export function nowMs(): number {
  return Date.now() + clockOffsetMs;
}

export function nowIso(): string {
  return new Date(nowMs()).toISOString();
}

export function getClockOffsetMs(): number {
  return clockOffsetMs;
}

export function useSalesState() {
  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
}

export function useSalesActing(): {
  rep: SalesRep | null;
  canActAsAnyone: boolean;
  self?: SalesRep;
} {
  const { currentRepId: stored } = useSalesState();
  const me = useSalesMeQuery();
  const reps = useSalesRepsQuery();
  const self = decorateRep(me.data?.rep) ?? undefined;
  const allow = me.data?.canActAs === true;
  if (!allow) return { rep: self ?? null, canActAsAnyone: false, self };
  if (!stored || stored === NO_ONE) return { rep: null, canActAsAnyone: true, self };
  const fromList = (reps.data ?? []).find((r) => r.id === stored);
  return {
    rep: fromList ?? decorateRep(me.data?.actingRep),
    canActAsAnyone: true,
    self,
  };
}

export function useCurrentRep(): SalesRep | null {
  return useSalesActing().rep;
}

export function requireActor(rep: SalesRep | null): string {
  if (!rep) fail('no_actor', 'Act as someone first.');
  return rep.id;
}

export function getReps(): SalesRep[] {
  return SALES_REPS;
}

export function getRep(id: string): SalesRep | undefined {
  return SALES_REPS.find((r) => r.id === id);
}

export function setCurrentRep(id: string): void {
  currentRepId = id;
  try {
    sessionStorage.setItem(REP_STORAGE_KEY, id);
  } catch {
    /* ignore */
  }
  actAsPrompt = null;
  emit();
}

/** Ask the viewer to pick a rep before an action that needs one, e.g.
 *  `promptActAs('claim a prospect')`. Picking does not replay the action. */
export function promptActAs(action: string): void {
  actAsPrompt = action;
  emit();
}

export function closeActAsPrompt(): void {
  actAsPrompt = null;
  emit();
}

export function getPolicy(): SalesPolicy {
  return policy;
}

export function isDerivedExpired(claim: SalesClaim, at = nowMs()): boolean {
  if (!ACTIVE_CLAIM_STATUSES.includes(claim.status)) return false;
  return new Date(claim.expiresAt).getTime() <= at;
}

export function isLockingClaim(claim: SalesClaim, at = nowMs()): boolean {
  return ACTIVE_CLAIM_STATUSES.includes(claim.status);
}

export function displayStatus(claim: SalesClaim, at = nowMs()): ClaimStatus {
  if (isDerivedExpired(claim, at) && claim.status !== 'expired') return 'expired';
  return claim.status;
}

export function achievedGoals(subjectId: string): SalesGoal[] {
  const goals = new Set<SalesGoal>();
  for (const c of claims) {
    if (c.subjectId === subjectId && c.status === 'won' && c.win) goals.add(c.win.goal);
  }
  return SALES_GOALS.filter((g) => goals.has(g));
}

export function lockingClaimForSubject(subjectId: string, at = nowMs()): SalesClaim | undefined {
  return claims.find((c) => c.subjectId === subjectId && isLockingClaim(c, at));
}

export function purposeById(id: string): SalesPurpose | undefined {
  return purposes.find((p) => p.id === id);
}

export function offerById(id: string | undefined): SalesOffer | undefined {
  if (!id) return undefined;
  return offers.find((o) => o.id === id);
}

export function subjectById(id: string): SalesSubject | undefined {
  return subjects.find((s) => s.id === id);
}

export function outreachById(id: string): SalesOutreach | undefined {
  return outreaches.find((o) => o.id === id);
}

export function touchesForState(): SalesTouch[] {
  return outreaches.map(outreachAsTouch).filter((t): t is SalesTouch => Boolean(t));
}

export function fitScore(subject: SalesSubject, goal: SalesGoal, weights: FitWeights = policy.fitWeights): number {
  const achieved = achievedGoals(subject.id);
  const goalGap = achieved.includes(goal) ? 0 : 1;
  const wsum = weights.areaDemand + weights.activityRecency + weights.spendBand + weights.goalGap || 1;
  const raw =
    (weights.areaDemand * subject.fitParts.areaDemand +
      weights.activityRecency * subject.fitParts.activityRecency +
      weights.spendBand * subject.fitParts.spendBand +
      weights.goalGap * goalGap) /
    wsum;
  return clamp01(raw);
}

export function bestFitScore(subject: SalesSubject): { goal: SalesGoal; score: number } {
  const remaining = SALES_GOALS.filter((g) => !achievedGoals(subject.id).includes(g));
  const goals = remaining.length ? remaining : SALES_GOALS;
  let best: { goal: SalesGoal; score: number } = { goal: goals[0], score: 0 };
  for (const g of goals) {
    const score = fitScore(subject, g);
    if (score > best.score) best = { goal: g, score };
  }
  return best;
}

function subjectWideCooling(subject: SalesSubject, at = nowMs()): boolean {
  if (!subject.subjectCooldownUntil) return false;
  return new Date(subject.subjectCooldownUntil).getTime() > at;
}

function goalCooling(subject: SalesSubject, goal: SalesGoal, at = nowMs()): boolean {
  const until = subject.goalCooldownUntil[goal];
  if (!until) return false;
  return new Date(until).getTime() > at;
}

export function availabilityOf(subject: SalesSubject, at = nowMs()): Availability {
  if (subject.doNotContact) return 'do_not_contact';
  if (lockingClaimForSubject(subject.id, at)) return 'locked';
  if (subjectWideCooling(subject, at)) return 'cooling';
  return 'available';
}

export function canReassign(actor: SalesRep | undefined, claim: SalesClaim): boolean {
  if (!actor) return false;
  if (actor.role === 'manager') return true;
  if (actor.role !== 'lead') return false;
  const owner = getRep(claim.ownerId);
  if (owner?.desk === actor.desk) return true;
  const subject = subjectById(claim.subjectId);
  return Boolean(subject && actor.desk !== 'all' && subject.verticals.includes(actor.desk));
}

export function canManageRules(role?: SalesRepRole | null): boolean {
  if (role == null) return true;
  return role === 'manager';
}

function collidingSubject(
  subject: SalesSubject,
  at = nowMs(),
): { other: SalesSubject; holder: SalesClaim } | null {
  const keys = new Set(subject.matchKeys);
  for (const other of subjects) {
    if (other.id === subject.id) continue;
    if (!other.matchKeys.some((k) => keys.has(k))) continue;
    const holder = lockingClaimForSubject(other.id, at);
    if (holder) return { other, holder };
  }
  return null;
}

function ownerActiveCount(ownerId: string, at = nowMs()): number {
  return claims.filter((c) => c.ownerId === ownerId && isLockingClaim(c, at)).length;
}

function addDays(isoFrom: string | number, days: number): string {
  const base = typeof isoFrom === 'number' ? isoFrom : new Date(isoFrom).getTime();
  return new Date(base + days * 86400_000).toISOString();
}

function addHours(isoFrom: string | number, hours: number): string {
  const base = typeof isoFrom === 'number' ? isoFrom : new Date(isoFrom).getTime();
  return new Date(base + hours * 3600_000).toISOString();
}

function appendEvent(
  type: SalesEventType,
  actorId: string,
  subjectId: string,
  claimId: string | undefined,
  payload: Record<string, unknown>,
): void {
  events = [
    {
      id: nextId('evt'),
      at: nowIso(),
      type,
      actorId,
      subjectId,
      claimId,
      payload,
    },
    ...events,
  ];
}

function patchSubject(id: string, patch: Partial<SalesSubject>): void {
  subjects = subjects.map((s) => (s.id === id ? { ...s, ...patch } : s));
}

function patchClaim(id: string, patch: Partial<SalesClaim>): SalesClaim {
  const existing = claims.find((c) => c.id === id);
  if (!existing) fail('not_found', 'Claim not found.');
  const updated = { ...existing, ...patch };
  claims = claims.map((c) => (c.id === id ? updated : c));
  return updated;
}

function refreshSubjectStage(subjectId: string): AcquisitionStage {
  const subject = subjectById(subjectId);
  if (!subject) return 'prospect';
  const wonMonetize = achievedGoals(subjectId).includes('monetize');
  const paid = (subject.platform?.lifetimePaidGhs ?? 0) > 0 || wonMonetize;
  let stage: AcquisitionStage;
  if (paid) stage = 'customer';
  else if (subject.platform?.uid != null || subject.platform?.oid != null) stage = 'user';
  else if (!subject.pooledAt) stage = 'research';
  else stage = 'prospect';
  if (stage !== subject.acquisitionStage) patchSubject(subjectId, { acquisitionStage: stage });
  return stage;
}

function emptyProfile(): SalesSubject['profile'] {
  return { autoFilled: [], manualOverrides: [] };
}

export function claimLead(input: {
  subjectId: string;
  ownerId: string;
  purposeId: string;
  offerId?: string;
}): SalesClaim {
  const at = nowMs();
  const subject = subjectById(input.subjectId);
  if (!subject) fail('not_found', 'Subject not found.');
  if (!isInPool(subject)) fail('not_pooled', 'Add this research hit to the pool before claiming.');
  const purpose = purposeById(input.purposeId);
  if (!purpose || !purpose.active) fail('missing_purpose', 'A purpose is required to claim.');
  if (subject.doNotContact) fail('do_not_contact', `${subject.displayName} is marked do-not-contact.`);
  if (subjectWideCooling(subject, at)) {
    fail('cooling', `${subject.displayName} is on cooldown until ${subject.subjectCooldownUntil}.`);
  }
  if (goalCooling(subject, purpose.goal, at)) {
    fail('cooling', `The ${purpose.goal} goal is cooling down for this lead.`);
  }
  if (achievedGoals(subject.id).includes(purpose.goal)) {
    fail('goal_achieved', `${purpose.goal} is already won for this lead.`);
  }
  const existing = lockingClaimForSubject(subject.id, at);
  if (existing) {
    const holder = getRep(existing.ownerId);
    fail('locked', `${subject.displayName} is held by ${holder?.name ?? existing.ownerId}.`, {
      holderRepId: existing.ownerId,
      holderName: holder?.name,
      claimId: existing.id,
    });
  }
  const collision = collidingSubject(subject, at);
  if (collision) {
    const holder = getRep(collision.holder.ownerId);
    fail(
      'identity_collision',
      `Same person as ${collision.other.displayName}, held by ${holder?.name ?? collision.holder.ownerId}.`,
      {
        otherSubjectId: collision.other.id,
        otherName: collision.other.displayName,
        holderRepId: collision.holder.ownerId,
        holderName: holder?.name,
      },
    );
  }
  if (ownerActiveCount(input.ownerId, at) >= policy.maxConcurrentPerRep) {
    fail('over_capacity', `At capacity (${policy.maxConcurrentPerRep} active claims).`);
  }
  if (input.offerId) {
    const offer = offerById(input.offerId);
    if (!offer || !offer.active) fail('offer_inactive', 'That offer is not available.');
    if (offer.capPerClaim < 1) fail('offer_cap', 'Offer cap is zero.');
  }

  const claimedAt = nowIso();
  const claim: SalesClaim = {
    id: nextId('clm'),
    subjectId: subject.id,
    ownerId: input.ownerId,
    purposeId: purpose.id,
    status: 'claimed',
    claimedAt,
    expiresAt: addDays(claimedAt, purpose.defaultClaimDays),
    offerId: input.offerId,
  };
  claims = [claim, ...claims];
  appendEvent('claim', input.ownerId, subject.id, claim.id, {
    purposeId: purpose.id,
    offerId: input.offerId,
  });
  emit();
  return claim;
}

function enforceOutreachGuards(subject: SalesSubject, channel: OutreachChannel, at: number): void {
  if (subject.doNotContact) fail('do_not_contact', `${subject.displayName} is marked do-not-contact.`);
  if (subjectWideCooling(subject, at)) {
    fail('cooling', `${subject.displayName} is on cooldown until ${subject.subjectCooldownUntil}.`);
  }
  if (channelNeedsConsent(channel) && subject.consent[channel] !== true) {
    fail('no_consent', `No ${channel} consent.`, { channel });
  }
  const cap = channelNeedsConsent(channel) ? policy.channelCaps[channel] : undefined;
  if (cap) {
    const windowMs = cap.days * 86400_000;
    const recent = outreaches.filter(
      (o) =>
        o.subjectId === subject.id &&
        o.channel === channel &&
        o.direction === 'outbound' &&
        o.status !== 'draft' &&
        o.status !== 'blocked' &&
        at - new Date(o.at).getTime() <= windowMs,
    );
    if (recent.length >= cap.max) {
      const oldest = recent.reduce((a, b) => (a.at < b.at ? a : b));
      const retryAt = new Date(new Date(oldest.at).getTime() + windowMs).toISOString();
      fail('channel_cap', `Max ${cap.max} ${channel} outreaches / ${cap.days} days.`, {
        channel,
        retryAt,
      });
    }
  }
  const minHours = policy.outreachMinHours[channel];
  if (minHours) {
    const last = outreaches
      .filter(
        (o) =>
          o.subjectId === subject.id &&
          o.channel === channel &&
          o.direction === 'outbound' &&
          o.status !== 'draft' &&
          o.status !== 'blocked',
      )
      .sort((a, b) => b.at.localeCompare(a.at))[0];
    if (last) {
      const elapsed = at - new Date(last.at).getTime();
      if (elapsed < minHours * 3600_000) {
        fail('outreach_spacing', `Wait ${minHours}h between ${channel} outreaches.`, {
          channel,
          retryAt: addHours(last.at, minHours),
        });
      }
    }
  }
}

function resolveOutreachMode(subject: SalesSubject, channel: OutreachChannel, mode?: OutreachMode): OutreachMode {
  if (mode) return mode;
  if (channel === 'in_app' || channel === 'tracked_link') return 'on_platform';
  return subject.platform?.uid != null ? 'on_platform' : 'off_platform';
}

export function defaultNextStepDue(): string {
  return addDays(nowIso(), policy.idleDays);
}

export function composeOutreach(input: {
  subjectId: string;
  actorId: string;
  channel: OutreachChannel;
  body: string;
  claimId?: string;
  templateId?: string;
  dest?: string;
  mode?: OutreachMode;
  nextStepDue?: string;
}): SalesOutreach {
  const subject = subjectById(input.subjectId);
  if (!subject) fail('not_found', 'Prospect not found.');
  if (!isInPool(subject)) fail('not_pooled', 'Add to the pool before composing outreach.');
  const body = input.body.trim();
  if (!body) fail('validation', 'Message body is required.');
  const outreach: SalesOutreach = {
    id: nextId('out'),
    subjectId: subject.id,
    claimId: input.claimId ?? lockingClaimForSubject(subject.id)?.id,
    actorId: input.actorId,
    channel: input.channel,
    mode: resolveOutreachMode(subject, input.channel, input.mode),
    status: 'draft',
    direction: 'outbound',
    body,
    templateId: input.templateId,
    trackedLinks: input.dest
      ? [
          {
            token: nextId('lnk').replace('lnk_', ''),
            dest: input.dest,
            expiresAt: addHours(nowIso(), policy.trackedLinkTtlHours),
            events: [],
          },
        ]
      : [],
    nextStepDue: input.nextStepDue,
    at: nowIso(),
  };
  outreaches = [outreach, ...outreaches];
  appendEvent('outreach', input.actorId, subject.id, outreach.claimId, {
    outreachId: outreach.id,
    status: 'draft',
    channel: input.channel,
  });
  emit();
  return outreach;
}

function patchOutreach(id: string, patch: Partial<SalesOutreach>): SalesOutreach {
  const existing = outreaches.find((o) => o.id === id);
  if (!existing) fail('not_found', 'Outreach not found.');
  const updated = { ...existing, ...patch };
  outreaches = outreaches.map((o) => (o.id === id ? updated : o));
  return updated;
}

function markClaimInPursuit(claimId: string | undefined): void {
  if (!claimId) return;
  const claim = claims.find((c) => c.id === claimId);
  if (claim && claim.status === 'claimed') patchClaim(claim.id, { status: 'in_pursuit' });
}

export function sendOnPlatform(outreachId: string, actorId: string, nextStepDue?: string): SalesOutreach {
  const at = nowMs();
  const outreach = outreachById(outreachId);
  if (!outreach) fail('not_found', 'Outreach not found.');
  if (outreach.status !== 'draft') fail('bad_status', 'Only a draft can be sent.');
  const subject = subjectById(outreach.subjectId);
  if (!subject) fail('not_found', 'Prospect not found.');
  if (subject.platform?.uid == null && outreach.channel === 'in_app') {
    fail('not_on_platform', 'In-app send needs a FeelyFeely user.');
  }
  try {
    enforceOutreachGuards(subject, outreach.channel, at);
  } catch (e) {
    const err = e instanceof SalesError ? e : null;
    const blocked = patchOutreach(outreach.id, {
      status: 'blocked',
      blockedReason: err?.message ?? String(e),
      actorId,
    });
    appendEvent('outreach', actorId, subject.id, outreach.claimId, {
      outreachId: outreach.id,
      status: 'blocked',
      reason: blocked.blockedReason,
    });
    emit();
    throw e;
  }
  const links =
    outreach.trackedLinks.length > 0
      ? outreach.trackedLinks
      : [
          {
            token: nextId('lnk').replace('lnk_', ''),
            dest: '/signup',
            expiresAt: addHours(nowIso(), policy.trackedLinkTtlHours),
            events: [],
          },
        ];
  const updated = patchOutreach(outreach.id, {
    status: 'sent',
    at: nowIso(),
    trackedLinks: links,
    actorId,
    nextStepDue: nextStepDue ?? outreach.nextStepDue ?? defaultNextStepDue(),
  });
  markClaimInPursuit(updated.claimId);
  appendEvent('outreach', actorId, subject.id, updated.claimId, {
    outreachId: updated.id,
    status: 'sent',
    channel: updated.channel,
    mode: 'on_platform',
  });
  emit();
  return updated;
}

export function logOffPlatform(input: {
  subjectId: string;
  actorId: string;
  channel: OutreachChannel;
  body: string;
  claimId?: string;
  templateId?: string;
  nextStepDue?: string;
}): SalesOutreach {
  const at = nowMs();
  const subject = subjectById(input.subjectId);
  if (!subject) fail('not_found', 'Prospect not found.');
  if (!isInPool(subject)) fail('not_pooled', 'Add to the pool before logging outreach.');
  const body = input.body.trim();
  if (!body) fail('validation', 'What happened is required.');
  const claimId = input.claimId ?? lockingClaimForSubject(subject.id)?.id;
  if (claimId) {
    const claim = claims.find((c) => c.id === claimId);
    if (!claim) fail('not_found', 'Claim not found.');
    if (isDerivedExpired(claim, at)) fail('expired', 'This claim has expired. Run an expiry sweep or release it.');
    if (!ACTIVE_CLAIM_STATUSES.includes(claim.status)) fail('terminal', 'Cannot touch a closed claim.');
  }
  enforceOutreachGuards(subject, input.channel, at);
  const outreach: SalesOutreach = {
    id: nextId('out'),
    subjectId: subject.id,
    claimId,
    actorId: input.actorId,
    channel: input.channel,
    mode: 'off_platform',
    status: 'logged',
    direction: 'outbound',
    body,
    templateId: input.templateId,
    trackedLinks: [],
    nextStepDue: input.nextStepDue ?? defaultNextStepDue(),
    at: nowIso(),
  };
  outreaches = [outreach, ...outreaches];
  markClaimInPursuit(claimId);
  appendEvent('outreach', input.actorId, subject.id, claimId, {
    outreachId: outreach.id,
    status: 'logged',
    channel: input.channel,
  });
  emit();
  return outreach;
}

export function logInbound(input: {
  subjectId: string;
  actorId: string;
  channel: OutreachChannel;
  body: string;
  claimId?: string;
}): SalesOutreach {
  const subject = subjectById(input.subjectId);
  if (!subject) fail('not_found', 'Prospect not found.');
  const body = input.body.trim();
  if (!body) fail('validation', 'What they said is required.');
  const outreach: SalesOutreach = {
    id: nextId('out'),
    subjectId: subject.id,
    claimId: input.claimId ?? lockingClaimForSubject(subject.id)?.id,
    actorId: input.actorId,
    channel: input.channel,
    mode: resolveOutreachMode(subject, input.channel),
    status: 'replied',
    direction: 'inbound',
    body,
    trackedLinks: [],
    at: nowIso(),
  };
  outreaches = [outreach, ...outreaches];
  appendEvent('outreach', input.actorId, subject.id, outreach.claimId, {
    outreachId: outreach.id,
    status: 'replied',
    direction: 'inbound',
    channel: input.channel,
  });
  emit();
  return outreach;
}

export function logTouch(input: {
  claimId: string;
  actorId: string;
  channel: TouchChannel;
  direction?: 'outbound' | 'inbound';
  summary: string;
  nextStepDue?: string;
}): SalesTouch {
  const claim = claims.find((c) => c.id === input.claimId);
  if (!claim) fail('not_found', 'Claim not found.');
  const outreach = logOffPlatform({
    subjectId: claim.subjectId,
    actorId: input.actorId,
    channel: input.channel,
    body: input.summary,
    claimId: input.claimId,
    nextStepDue: input.nextStepDue,
  });
  const touch = outreachAsTouch(outreach);
  if (!touch) fail('terminal', 'Could not log touch.');
  return touch;
}

export function recordLinkEvent(token: string, kind: 'open' | 'click'): SalesOutreach {
  const outreach = outreaches.find((o) => o.trackedLinks.some((l) => l.token === token));
  if (!outreach) fail('not_found', 'Tracked link not found.');
  const link = outreach.trackedLinks.find((l) => l.token === token)!;
  if (new Date(link.expiresAt).getTime() < nowMs()) fail('expired', 'This tracked link has expired.');
  const nextLinks = outreach.trackedLinks.map((l) =>
    l.token === token ? { ...l, events: [...l.events, { kind, at: nowIso() }] } : l,
  );
  let status: OutreachStatus = outreach.status;
  if (kind === 'open' && (status === 'sent' || status === 'logged')) status = 'opened';
  if (kind === 'click' && status !== 'replied') status = 'clicked';
  const updated = patchOutreach(outreach.id, { trackedLinks: nextLinks, status });
  appendEvent('link_event', 'system', outreach.subjectId, outreach.claimId, {
    outreachId: outreach.id,
    token,
    kind,
  });
  emit();
  return updated;
}

export function recordFeedback(
  outreachId: string,
  actorId: string,
  input: { sentiment: OutreachFeedbackSentiment; outcome: OutreachFeedbackOutcome; note?: string },
): SalesOutreach {
  const outreach = outreachById(outreachId);
  if (!outreach) fail('not_found', 'Outreach not found.');
  if (outreach.status === 'draft' || outreach.status === 'blocked') {
    fail('bad_status', 'Log or send outreach before recording feedback.');
  }
  const updated = patchOutreach(outreachId, {
    feedback: { ...input, at: nowIso() },
    status: input.outcome === 'interested' || input.outcome === 'joined' ? 'replied' : outreach.status,
    actorId,
  });
  appendEvent('outreach', actorId, outreach.subjectId, outreach.claimId, {
    outreachId,
    feedback: input.outcome,
  });
  emit();
  return updated;
}

export function latestOutreach(subjectId: string): SalesOutreach | undefined {
  return outreaches
    .filter((o) => o.subjectId === subjectId)
    .sort((a, b) => b.at.localeCompare(a.at))[0];
}

export function pendingOutreachFeedback(subjectId: string): SalesOutreach | undefined {
  const last = latestOutreach(subjectId);
  if (!last) return undefined;
  if (last.feedback || last.status === 'blocked' || last.status === 'draft') return undefined;
  return last;
}

export function dueOutreaches(ownerId?: string): SalesOutreach[] {
  const at = nowMs();
  return outreaches
    .filter((o) => {
      if (!o.nextStepDue) return false;
      if (o.status === 'blocked' || o.status === 'draft') return false;
      if (ownerId) {
        const claim = o.claimId ? claims.find((c) => c.id === o.claimId) : lockingClaimForSubject(o.subjectId);
        if (claim?.ownerId !== ownerId) return false;
      }
      return new Date(o.nextStepDue).getTime() <= at + 48 * 3600_000;
    })
    .sort((a, b) => (a.nextStepDue ?? '').localeCompare(b.nextStepDue ?? ''));
}

export function addFromResearch(subjectId: string, actorId: string): SalesSubject {
  const at = nowMs();
  const subject = subjectById(subjectId);
  if (!subject) fail('not_found', 'Research hit not found.');
  if (isInPool(subject)) fail('already_pooled', `${subject.displayName} is already in the pool.`);
  if (subject.doNotContact) fail('do_not_contact', `${subject.displayName} is marked do-not-contact.`);
  if (subjectWideCooling(subject, at)) {
    fail('cooling', `${subject.displayName} is on cooldown.`);
  }
  const collision = collidingSubject(subject, at);
  if (collision) {
    const holder = getRep(collision.holder.ownerId);
    fail(
      'identity_collision',
      `Same person as ${collision.other.displayName}, held by ${holder?.name ?? collision.holder.ownerId}.`,
    );
  }
  const pooledAt = nowIso();
  patchSubject(subjectId, {
    pooledAt,
    addedById: actorId,
    acquisitionStage: deriveAcquisitionStage({ ...subject, pooledAt }),
    researchCooldownUntil: addDays(pooledAt, policy.researchResurfaceDays),
  });
  refreshSubjectStage(subjectId);
  appendEvent('pool_add', actorId, subjectId, undefined, { from: 'research' });
  emit();
  return subjectById(subjectId)!;
}

export function dismissResearch(subjectId: string, actorId: string): SalesSubject {
  const subject = subjectById(subjectId);
  if (!subject) fail('not_found', 'Research hit not found.');
  if (isInPool(subject)) fail('already_pooled', 'Already in the pool.');
  const until = addDays(nowIso(), policy.researchResurfaceDays);
  patchSubject(subjectId, { researchCooldownUntil: until });
  appendEvent('research_dismiss', actorId, subjectId, undefined, { until });
  emit();
  return subjectById(subjectId)!;
}

let marketplaceSkipUntil = new Map<string, string>();

function marketplaceHitSkipped(hitId: string, subject: SalesSubject | undefined, at: number): boolean {
  const until = marketplaceSkipUntil.get(hitId);
  if (until && new Date(until).getTime() > at) return true;
  return Boolean(subject?.researchCooldownUntil && new Date(subject.researchCooldownUntil).getTime() > at);
}

export type MarketplaceRow = MarketplaceHit & {
  subject?: SalesSubject;
  inPool: boolean;
  skipped: boolean;
};

function subjectForHit(hit: MarketplaceHit): SalesSubject | undefined {
  return subjects.find((s) => hitMatchesSubject(hit, s));
}

export function searchMarketplace(query: MarketplaceQuery = {}): MarketplaceRow[] {
  const at = nowMs();
  const q = query.q?.trim().toLowerCase() ?? '';
  const recencyMs = query.recencyDays === 'all' || query.recencyDays == null ? null : query.recencyDays * 86400_000;
  const sort = query.sort ?? 'value';

  const rows: MarketplaceRow[] = MARKETPLACE_HITS.map((hit) => {
    const subject = subjectForHit(hit);
    const skipped = marketplaceHitSkipped(hit.id, subject, at);
    return {
      ...hit,
      subject,
      inPool: Boolean(subject && isInPool(subject)),
      skipped,
    };
  });

  const filtered = rows.filter((row) => {
    if (row.skipped) return false;
    if (query.marketplace && query.marketplace !== 'all' && row.marketplace !== query.marketplace) return false;
    if (query.vertical && query.vertical !== 'all' && !row.verticals.includes(query.vertical)) return false;
    if (query.listingCategory && query.listingCategory !== 'all' && !row.categories.includes(query.listingCategory)) return false;
    if (query.region && query.region !== 'all' && row.region !== query.region) return false;
    if (query.tier === 'paying' && !(row.paid || row.premium)) return false;
    if (query.tier === 'premium' && !row.premium) return false;
    if (query.tier === 'free' && (row.paid || row.premium)) return false;
    if (query.hasPhone && !row.phone) return false;
    if (query.notInPool && row.inPool) return false;
    if (recencyMs != null) {
      const seen = new Date(row.lastSeen).getTime();
      if (!Number.isFinite(seen) || at - seen > recencyMs) return false;
    }
    if (q) {
      const blob = [row.displayName, row.phone, row.region, ...row.categories]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      if (!blob.includes(q)) return false;
    }
    return true;
  });

  return filtered.sort((a, b) => {
    if (sort === 'count') return b.listingCount - a.listingCount;
    if (sort === 'recent') return new Date(b.lastSeen).getTime() - new Date(a.lastSeen).getTime();
    if (sort === 'spend') return b.promoSpend.monthlyGhs - a.promoSpend.monthlyGhs;
    return b.inventoryValueGhs - a.inventoryValueGhs;
  });
}

export function marketplaceOverview(): {
  sellers: number;
  paying: number;
  listings: number;
  inventoryValueGhs: number;
  estimatedSpendGhs: number;
  estimatedSpendLifetimeGhs: number;
  open: number;
} {
  const at = nowMs();
  let paying = 0;
  let listings = 0;
  let inventoryValueGhs = 0;
  let estimatedSpendGhs = 0;
  let estimatedSpendLifetimeGhs = 0;
  let open = 0;
  for (const hit of MARKETPLACE_HITS) {
    const subject = subjectForHit(hit);
    const skipped = marketplaceHitSkipped(hit.id, subject, at);
    if (hit.paid || hit.premium) paying += 1;
    listings += hit.listingCount;
    inventoryValueGhs += hit.inventoryValueGhs;
    estimatedSpendGhs += hit.promoSpend.monthlyGhs;
    estimatedSpendLifetimeGhs += hit.promoSpend.lifetimeGhs;
    if (!skipped && !(subject && isInPool(subject))) open += 1;
  }
  return { sellers: MARKETPLACE_HITS.length, paying, listings, inventoryValueGhs, estimatedSpendGhs, estimatedSpendLifetimeGhs, open };
}

export function addFromMarketplace(hitId: string, actorId: string): SalesSubject {
  const hit = marketplaceHitById(hitId);
  if (!hit) fail('not_found', 'Marketplace listing not found.');
  marketplaceSkipUntil.delete(hitId);

  const existing = subjectForHit(hit);
  if (existing) {
    if (isInPool(existing)) fail('already_pooled', `${existing.displayName} is already in the pool.`);
    patchSubject(existing.id, {
      source: { system: 'ff-prospect', collection: hit.collection, externalId: hit.externalId },
      activity: {
        listingCount: hit.listingCount,
        lastSeen: hit.lastSeen,
        inventoryValueGhs: hit.inventoryValueGhs,
        paidOnSource: hit.paid,
        premium: hit.premium,
        listingsLast30: hit.activity.listingsLast30,
        avgDaysBetween: hit.activity.avgDaysBetween ?? undefined,
        listedSince: hit.activity.listedSince,
        estimatedSpendGhs: hit.promoSpend.monthlyGhs,
        estimatedSpendLifetimeGhs: hit.promoSpend.lifetimeGhs,
        boostPlan: hit.promoSpend.plan,
        boostsLast30: hit.promoSpend.boostsLast30,
        listingCategories: hit.categories,
      },
      spendBand: hit.spendBand,
    });
    return addFromResearch(existing.id, actorId);
  }

  const jijiUserId = hit.marketplace === 'jiji' ? hit.externalId : undefined;
  const meqasaAgentKey = hit.collection.startsWith('meqasa') ? hit.externalId : undefined;
  const matchKeys = buildMatchKeys({
    phone: hit.phone,
    email: hit.email,
    jijiUserId,
    meqasaAgentKey,
  });
  const lockedHit = subjects.find((s) => s.matchKeys.some((k) => matchKeys.includes(k)) && lockingClaimForSubject(s.id));
  if (lockedHit) {
    const lock = lockingClaimForSubject(lockedHit.id);
    const holder = lock ? getRep(lock.ownerId) : undefined;
    fail('identity_collision', `Same person as ${lockedHit.displayName}, held by ${holder?.name ?? 'another rep'}.`, {
      otherSubjectId: lockedHit.id,
    });
  }
  const pooled = subjects.find((s) => isInPool(s) && s.matchKeys.some((k) => matchKeys.includes(k)));
  if (pooled) fail('duplicate', `Already in pool as ${pooled.displayName}.`, { otherSubjectId: pooled.id });

  const pooledAt = nowIso();
  const created: SalesSubject = {
    id: nextId('sub'),
    kind: hit.kind,
    displayName: hit.displayName,
    phone: hit.phone,
    email: hit.email,
    matchKeys,
    areas: [{ region: hit.region }],
    verticals: hit.verticals,
    spendBand: hit.spendBand,
    fitParts: {
      areaDemand: 0.7,
      activityRecency: 0.85,
      spendBand: spendBandPart(hit.spendBand),
    },
    activity: {
      listingCount: hit.listingCount,
      lastSeen: hit.lastSeen,
      inventoryValueGhs: hit.inventoryValueGhs,
      paidOnSource: hit.paid,
      premium: hit.premium,
      listingsLast30: hit.activity.listingsLast30,
      avgDaysBetween: hit.activity.avgDaysBetween ?? undefined,
      listedSince: hit.activity.listedSince,
      estimatedSpendGhs: hit.promoSpend.monthlyGhs,
      estimatedSpendLifetimeGhs: hit.promoSpend.lifetimeGhs,
      boostPlan: hit.promoSpend.plan,
      boostsLast30: hit.promoSpend.boostsLast30,
      listingCategories: hit.categories,
    },
    source: { system: 'ff-prospect', collection: hit.collection, externalId: hit.externalId },
    consent: hit.phone ? { phone: true, whatsapp: true } : {},
    doNotContact: false,
    goalCooldownUntil: {},
    createdAt: pooledAt,
    pooledAt,
    addedById: actorId,
    acquisitionStage: 'prospect',
    profile: {
      notes: `Active elsewhere · ${hit.categories.join(', ') || 'listings'}${
        hit.promoSpend.monthlyGhs
          ? ` · promo ~GHS ${hit.promoSpend.monthlyGhs.toLocaleString()}/mo, ~GHS ${hit.promoSpend.lifetimeGhs.toLocaleString()} lifetime`
          : ''
      }.`,
      autoFilled: ['displayName', 'phone', 'areas', 'verticals', 'spendBand'],
      lastEnrichedAt: pooledAt,
      manualOverrides: [],
    },
  };
  created.acquisitionStage = deriveAcquisitionStage(created);
  subjects = [created, ...subjects];
  appendEvent('pool_add', actorId, created.id, undefined, { from: 'ff-prospect', hitId });
  emit();
  return created;
}

export function skipMarketplace(hitId: string, actorId: string): void {
  const hit = marketplaceHitById(hitId);
  if (!hit) fail('not_found', 'Marketplace listing not found.');
  const existing = subjectForHit(hit);
  if (existing && isInPool(existing)) fail('already_pooled', 'Already in the pool.');
  if (existing && existing.acquisitionStage === 'research') {
    dismissResearch(existing.id, actorId);
    return;
  }
  const until = addDays(nowIso(), policy.researchResurfaceDays);
  marketplaceSkipUntil.set(hitId, until);
  if (existing) {
    patchSubject(existing.id, { researchCooldownUntil: until });
    appendEvent('research_dismiss', actorId, existing.id, undefined, { hitId, until });
  }
  emit();
}

export function updateProspectProfile(
  subjectId: string,
  actorId: string,
  patch: Partial<Pick<SalesSubject, 'displayName' | 'phone' | 'email' | 'kind' | 'spendBand' | 'consent' | 'doNotContact'>> & {
    notes?: string;
    region?: string;
    uid?: number | null;
    oid?: number | null;
  },
): SalesSubject {
  const subject = subjectById(subjectId);
  if (!subject) fail('not_found', 'Prospect not found.');
  const overrides = new Set(subject.profile.manualOverrides);
  const next: Partial<SalesSubject> = {};
  const mark = (field: ProfileField, changed: boolean) => {
    if (changed && subject.profile.autoFilled.includes(field)) overrides.add(field);
  };
  if (patch.displayName != null && patch.displayName.trim()) {
    mark('displayName', patch.displayName.trim() !== subject.displayName);
    next.displayName = patch.displayName.trim();
  }
  if (patch.phone !== undefined) {
    mark('phone', patch.phone !== subject.phone);
    next.phone = patch.phone || undefined;
  }
  if (patch.email !== undefined) {
    mark('email', patch.email !== subject.email);
    next.email = patch.email || undefined;
  }
  if (patch.kind && patch.kind !== subject.kind) {
    mark('kind', true);
    next.kind = patch.kind;
  }
  if (patch.spendBand && patch.spendBand !== subject.spendBand) {
    mark('spendBand', true);
    next.spendBand = patch.spendBand;
  }
  if (patch.region != null) {
    mark('areas', patch.region !== subject.areas[0]?.region);
    next.areas = patch.region ? [{ region: patch.region }] : [];
  }
  if (patch.consent) {
    next.consent = { ...subject.consent, ...patch.consent };
  }
  if (patch.doNotContact !== undefined) {
    next.doNotContact = patch.doNotContact;
  }
  if (patch.uid !== undefined || patch.oid !== undefined) {
    const uid = patch.uid === null ? undefined : patch.uid !== undefined ? patch.uid : subject.platform?.uid;
    const oid = patch.oid === null ? undefined : patch.oid !== undefined ? patch.oid : subject.platform?.oid;
    next.platform =
      uid != null || oid != null
        ? {
            uid,
            oid,
            listingCount: subject.platform?.listingCount ?? 0,
            lifetimePaidGhs: subject.platform?.lifetimePaidGhs ?? 0,
            lastActiveAt: subject.platform?.lastActiveAt ?? nowIso(),
          }
        : undefined;
  }
  const notes = patch.notes;
  next.profile = {
    ...subject.profile,
    notes: notes !== undefined ? notes : subject.profile.notes,
    manualOverrides: Array.from(overrides),
  };
  if (notes !== undefined && notes !== subject.profile.notes) {
    next.profile.manualOverrides = Array.from(new Set([...overrides, 'notes' as ProfileField]));
  }
  const phone = (next.phone as string | undefined) ?? subject.phone;
  const email = (next.email as string | undefined) ?? subject.email;
  const uid = next.platform ? next.platform.uid : patch.uid === undefined ? subject.platform?.uid : undefined;
  const oid = next.platform ? next.platform.oid : patch.oid === undefined ? subject.platform?.oid : undefined;
  if (next.phone !== undefined || next.email !== undefined || patch.uid !== undefined || patch.oid !== undefined) {
    next.matchKeys = buildMatchKeys({ phone, email, uid, oid });
  }
  patchSubject(subjectId, next);
  refreshSubjectStage(subjectId);
  appendEvent('profile_edit', actorId, subjectId, undefined, { fields: Object.keys(patch) });
  emit();
  return subjectById(subjectId)!;
}

export function pursueClaim(claimId: string, actorId: string): SalesClaim {
  const claim = claims.find((c) => c.id === claimId);
  if (!claim) fail('not_found', 'Claim not found.');
  if (isDerivedExpired(claim)) fail('expired', 'Claim has expired.');
  if (claim.ownerId !== actorId) fail('not_owner', 'Only the owner can move this claim.');
  if (claim.status !== 'claimed') fail('bad_status', 'Can only start pursuit from a fresh claim.');
  const updated = patchClaim(claimId, { status: 'in_pursuit' });
  appendEvent('pursue', actorId, claim.subjectId, claimId, {});
  emit();
  return updated;
}

export function markCommitted(claimId: string, actorId: string): SalesClaim {
  const claim = claims.find((c) => c.id === claimId);
  if (!claim) fail('not_found', 'Claim not found.');
  if (isDerivedExpired(claim)) fail('expired', 'Claim has expired.');
  if (claim.status !== 'in_pursuit' && claim.status !== 'claimed') fail('bad_status', 'Can only commit an active pursuit.');
  const updated = patchClaim(claimId, { status: 'committed' });
  appendEvent('commit', actorId, claim.subjectId, claimId, {});
  emit();
  return updated;
}

export function attachOffer(claimId: string, offerId: string, actorId: string): SalesClaim {
  const claim = claims.find((c) => c.id === claimId);
  if (!claim) fail('not_found', 'Claim not found.');
  if (!ACTIVE_CLAIM_STATUSES.includes(claim.status)) fail('terminal', 'Cannot attach an offer to a closed claim.');
  const offer = offerById(offerId);
  if (!offer || !offer.active) fail('offer_inactive', 'That offer is not available.');
  const updated = patchClaim(claimId, { offerId });
  appendEvent('offer_attach', actorId, claim.subjectId, claimId, { offerId });
  emit();
  return updated;
}

export function winClaim(claimId: string, actorId: string, evidence: Omit<ClaimWin, 'at' | 'goal'>): SalesClaim {
  const claim = claims.find((c) => c.id === claimId);
  if (!claim) fail('not_found', 'Claim not found.');
  if (isDerivedExpired(claim)) fail('expired', 'Claim has expired.');
  if (!ACTIVE_CLAIM_STATUSES.includes(claim.status)) fail('bad_status', 'Can only win an active claim.');
  const purpose = purposeById(claim.purposeId);
  if (!purpose) fail('missing_purpose', 'Purpose missing.');
  if (purpose.goal === 'signup' && evidence.uid == null) fail('evidence', 'Signup win needs a uid.');
  if (purpose.goal === 'supply' && !evidence.listingId) fail('evidence', 'Supply win needs a listing id.');
  if (purpose.goal === 'monetize' && !evidence.promotionId) fail('evidence', 'Monetize win needs a promotion id.');

  const win: ClaimWin = { ...evidence, goal: purpose.goal, at: nowIso() };
  const updated = patchClaim(claimId, { status: 'won', win });
  const subject = subjectById(claim.subjectId);
  if (subject) {
    patchSubject(subject.id, {
      goalCooldownUntil: {
        ...subject.goalCooldownUntil,
        [purpose.goal]: addDays(nowIso(), policy.wonSameGoalDays),
      },
    });
    if (purpose.goal === 'signup' && evidence.uid != null) {
      patchSubject(subject.id, {
        platform: {
          uid: evidence.uid,
          oid: evidence.oid ?? subject.platform?.oid,
          listingCount: subject.platform?.listingCount ?? 0,
          lastActiveAt: nowIso(),
          lifetimePaidGhs: subject.platform?.lifetimePaidGhs ?? 0,
        },
      });
    }
    if (purpose.goal === 'monetize') {
      const current = subjectById(subject.id);
      patchSubject(subject.id, {
        platform: {
          uid: current?.platform?.uid,
          oid: evidence.oid ?? current?.platform?.oid,
          listingCount: current?.platform?.listingCount ?? 0,
          lastActiveAt: nowIso(),
          lifetimePaidGhs: Math.max(current?.platform?.lifetimePaidGhs ?? 0, 1),
        },
      });
    }
    refreshSubjectStage(subject.id);
  }
  appendEvent('win', actorId, claim.subjectId, claimId, { ...win });
  emit();
  return updated;
}

export function advanceClaim(claimId: string, nextPurposeId: string, actorId: string): SalesClaim {
  const claim = claims.find((c) => c.id === claimId);
  if (!claim) fail('not_found', 'Claim not found.');
  if (claim.status !== 'won') fail('bad_status', 'Can only advance a won claim.');
  if (claim.ownerId !== actorId) fail('not_owner', 'Only the owner can advance this claim.');
  const next = purposeById(nextPurposeId);
  if (!next || !next.active) fail('missing_purpose', 'Next purpose is required.');
  if (achievedGoals(claim.subjectId).includes(next.goal)) {
    fail('goal_achieved', `${next.goal} is already won for this lead.`);
  }
  if (claim.win && policy.nextGoalHandoffDays > 0) {
    const ready = new Date(claim.win.at).getTime() + policy.nextGoalHandoffDays * 86400_000;
    if (nowMs() < ready) {
      fail('handoff_wait', `Next goal opens ${new Date(ready).toLocaleDateString('en-GB')}.`);
    }
  }
  const created = claimLead({
    subjectId: claim.subjectId,
    ownerId: claim.ownerId,
    purposeId: next.id,
  });
  appendEvent('advance', actorId, claim.subjectId, created.id, {
    fromClaimId: claim.id,
    purposeId: next.id,
  });
  emit();
  return created;
}

function closeWithSubjectCooldown(claim: SalesClaim, days: number): void {
  const until = addDays(nowIso(), days);
  const subject = subjectById(claim.subjectId);
  if (subject) patchSubject(subject.id, { subjectCooldownUntil: until });
}

export function releaseClaim(claimId: string, actorId: string): SalesClaim {
  const claim = claims.find((c) => c.id === claimId);
  if (!claim) fail('not_found', 'Claim not found.');
  if (!ACTIVE_CLAIM_STATUSES.includes(claim.status) && !isDerivedExpired(claim)) {
    fail('bad_status', 'Can only release an active claim.');
  }
  const last = latestOutreach(claim.subjectId);
  if (last && !last.feedback && last.status !== 'blocked' && last.status !== 'draft') {
    fail('feedback_required', 'Record outreach feedback before releasing.');
  }
  const updated = patchClaim(claimId, { status: 'released' });
  closeWithSubjectCooldown(claim, policy.releaseDays);
  appendEvent('release', actorId, claim.subjectId, claimId, {});
  emit();
  return updated;
}

export function loseClaim(claimId: string, actorId: string, reason: LossReason, note?: string): SalesClaim {
  if (!LOSS_REASONS.includes(reason)) fail('loss_reason', 'A structured loss reason is required.');
  const claim = claims.find((c) => c.id === claimId);
  if (!claim) fail('not_found', 'Claim not found.');
  if (!ACTIVE_CLAIM_STATUSES.includes(claim.status) && !isDerivedExpired(claim)) {
    fail('bad_status', 'Can only lose an active claim.');
  }
  const last = latestOutreach(claim.subjectId);
  if (last && !last.feedback && last.status !== 'blocked' && last.status !== 'draft') {
    fail('feedback_required', 'Record outreach feedback before marking lost.');
  }
  const updated = patchClaim(claimId, { status: 'lost', lossReason: reason, lossNote: note });
  closeWithSubjectCooldown(claim, policy.lostDays);
  appendEvent('lose', actorId, claim.subjectId, claimId, { reason, note });
  emit();
  return updated;
}

export function reassignClaim(claimId: string, actorId: string, newOwnerId: string): SalesClaim {
  const actor = getRep(actorId);
  const claim = claims.find((c) => c.id === claimId);
  if (!claim) fail('not_found', 'Claim not found.');
  if (!canReassign(actor, claim)) fail('not_manager', 'Only a lead or manager can reassign this claim.');
  if (!isLockingClaim(claim) && !isDerivedExpired(claim)) fail('bad_status', 'Can only reassign an active lock.');
  if (!getRep(newOwnerId)) fail('unknown_rep', 'Unknown sales rep.');
  if (ownerActiveCount(newOwnerId) >= policy.maxConcurrentPerRep && newOwnerId !== claim.ownerId) {
    fail('over_capacity', 'New owner is at capacity.');
  }
  const updated = patchClaim(claimId, { ownerId: newOwnerId });
  appendEvent('reassign', actorId, claim.subjectId, claimId, { from: claim.ownerId, to: newOwnerId });
  emit();
  return updated;
}

export function runExpirySweep(actorId: string): number {
  const at = nowMs();
  let n = 0;
  for (const claim of [...claims]) {
    if (!isDerivedExpired(claim, at)) continue;
    if (claim.status === 'expired') continue;
    patchClaim(claim.id, { status: 'expired' });
    closeWithSubjectCooldown(claim, policy.expiredDays);
    appendEvent('expire', actorId, claim.subjectId, claim.id, {});
    n += 1;
  }
  if (n) emit();
  return n;
}

export function simulateDays(days: number, actorId: string): { expired: number } {
  clockOffsetMs += days * 86400_000;
  const expired = runExpirySweep(actorId);
  emit();
  return { expired };
}

export function resetClock(): void {
  clockOffsetMs = 0;
  emit();
}

export function updatePolicy(patch: Partial<SalesPolicy>): SalesPolicy {
  const next = { ...policy, ...patch };
  if (patch.fitWeights) {
    const w = { ...policy.fitWeights, ...patch.fitWeights };
    const sum = w.areaDemand + w.activityRecency + w.spendBand + w.goalGap || 1;
    next.fitWeights = {
      areaDemand: w.areaDemand / sum,
      activityRecency: w.activityRecency / sum,
      spendBand: w.spendBand / sum,
      goalGap: w.goalGap / sum,
    };
  }
  if (patch.channelCaps) next.channelCaps = { ...policy.channelCaps, ...patch.channelCaps };
  if (patch.outreachMinHours) next.outreachMinHours = { ...policy.outreachMinHours, ...patch.outreachMinHours };
  policy = next;
  emit();
  return policy;
}

export function upsertPurpose(input: Partial<SalesPurpose> & { id?: string }): SalesPurpose {
  if (input.id) {
    const existing = purposes.find((p) => p.id === input.id);
    if (existing) {
      const updated = { ...existing, ...input };
      purposes = purposes.map((p) => (p.id === existing.id ? updated : p));
      emit();
      return updated;
    }
  }
  const created: SalesPurpose = {
    id: input.id ?? nextId('pur'),
    name: input.name ?? 'Untitled purpose',
    goal: input.goal ?? 'signup',
    vertical: input.vertical,
    targetOutcome: input.targetOutcome ?? 'account_created',
    defaultClaimDays: input.defaultClaimDays ?? 7,
    estimatedValueGhs: input.estimatedValueGhs ?? 0,
    nativeMetric: input.nativeMetric,
    active: input.active ?? true,
  };
  purposes = [created, ...purposes];
  emit();
  return created;
}

export function upsertOffer(input: Partial<SalesOffer> & { id?: string }): SalesOffer {
  if (input.id) {
    const existing = offers.find((o) => o.id === input.id);
    if (existing) {
      const updated = { ...existing, ...input };
      offers = offers.map((o) => (o.id === existing.id ? updated : o));
      emit();
      return updated;
    }
  }
  const created: SalesOffer = {
    id: input.id ?? nextId('off'),
    name: input.name ?? 'Untitled offer',
    kind: input.kind ?? 'voucher',
    costGhs: input.costGhs ?? 0,
    capPerClaim: input.capPerClaim ?? 1,
    active: input.active ?? true,
    modulePath: input.modulePath ?? '/admin/vouchers',
  };
  offers = [created, ...offers];
  emit();
  return created;
}

function newPooledSubject(input: {
  displayName: string;
  phone?: string;
  email?: string;
  kind: SubjectKind;
  region?: string;
  vertical: SalesVertical;
  spendBand: SpendBand;
  source: SalesSubject['source'];
  uid?: number;
  oid?: number;
  actorId: string;
  notes?: string;
}): SalesSubject {
  const matchKeys = buildMatchKeys({
    phone: input.phone,
    email: input.email,
    uid: input.uid,
    oid: input.oid,
  });
  const lockedHit = subjects.find((s) => s.matchKeys.some((k) => matchKeys.includes(k)) && lockingClaimForSubject(s.id));
  if (lockedHit) {
    const lock = lockingClaimForSubject(lockedHit.id);
    const holder = lock ? getRep(lock.ownerId) : undefined;
    fail('identity_collision', `Same person as ${lockedHit.displayName}, held by ${holder?.name ?? 'another rep'}.`, {
      otherSubjectId: lockedHit.id,
    });
  }
  const existing = subjects.find((s) => s.matchKeys.some((k) => matchKeys.includes(k)));
  if (existing) {
    fail('duplicate', `Already in pool as ${existing.displayName}.`, { otherSubjectId: existing.id });
  }
  const pooledAt = nowIso();
  const created: SalesSubject = {
    id: nextId('sub'),
    kind: input.kind,
    displayName: input.displayName,
    phone: input.phone,
    email: input.email,
    matchKeys,
    areas: input.region ? [{ region: input.region }] : [],
    verticals: [input.vertical],
    spendBand: input.spendBand,
    fitParts: {
      areaDemand: 0.5,
      activityRecency: 0.5,
      spendBand: spendBandPart(input.spendBand),
    },
    activity: { listingCount: 0 },
    platform:
      input.uid != null || input.oid != null
        ? { uid: input.uid, oid: input.oid, listingCount: 0, lifetimePaidGhs: 0, lastActiveAt: pooledAt }
        : undefined,
    source: input.source,
    consent: {},
    doNotContact: false,
    goalCooldownUntil: {},
    createdAt: pooledAt,
    pooledAt,
    addedById: input.actorId,
    acquisitionStage: 'prospect',
    profile: {
      ...emptyProfile(),
      notes: input.notes,
      autoFilled: [],
    },
  };
  created.acquisitionStage = deriveAcquisitionStage(created);
  subjects = [created, ...subjects];
  return created;
}

export type CsvImportRow = {
  line: number;
  displayName: string;
  phone: string;
  email: string;
  kind: SubjectKind;
  region: string;
  vertical: SalesVertical;
  spendBand: SpendBand;
  error?: string;
};

function splitCsvLine(line: string): string[] {
  const out: string[] = [];
  let cur = '';
  let quoted = false;
  for (let i = 0; i < line.length; i++) {
    const ch = line[i];
    if (quoted) {
      if (ch === '"') {
        if (line[i + 1] === '"') {
          cur += '"';
          i += 1;
        } else {
          quoted = false;
        }
      } else {
        cur += ch;
      }
    } else if (ch === '"') {
      quoted = true;
    } else if (ch === ',') {
      out.push(cur.trim());
      cur = '';
    } else {
      cur += ch;
    }
  }
  out.push(cur.trim());
  return out;
}

export function parseProspectCsv(text: string): CsvImportRow[] {
  const lines = text.split(/\r?\n/).filter((l) => l.trim());
  if (lines.length === 0) return [];
  const start = lines[0].toLowerCase().includes('name') ? 1 : 0;
  const rows: CsvImportRow[] = [];
  for (let i = start; i < lines.length; i++) {
    const [name = '', phone = '', email = '', kindRaw = '', region = '', verticalRaw = '', spendRaw = ''] = splitCsvLine(
      lines[i],
    );
    const kind = (['person', 'organization', 'business'].includes(kindRaw) ? kindRaw : 'business') as SubjectKind;
    const vertical = (['homes', 'market', 'locals', 'places'].includes(verticalRaw)
      ? verticalRaw
      : 'homes') as SalesVertical;
    const spendBand = (['low', 'mid', 'high', 'unknown'].includes(spendRaw) ? spendRaw : 'unknown') as SpendBand;
    rows.push({
      line: i + 1,
      displayName: name,
      phone,
      email,
      kind,
      region,
      vertical,
      spendBand,
      error: name ? undefined : 'Missing name',
    });
  }
  return rows;
}

export function previewCsvImport(text: string): CsvImportRow[] {
  return parseProspectCsv(text).map((row) => {
    if (row.error) return row;
    const keys = buildMatchKeys({ phone: row.phone || undefined, email: row.email || undefined });
    if (keys.length === 0) return row;
    const existing = subjects.find((s) => s.matchKeys.some((k) => keys.includes(k)));
    if (existing) return { ...row, error: `Already in pool as ${existing.displayName}` };
    return row;
  });
}

export function importCsv(
  text: string,
  actorId: string,
): { imported: number; skipped: { line: number; reason: string }[] } {
  const skipped: { line: number; reason: string }[] = [];
  let imported = 0;
  for (const row of parseProspectCsv(text)) {
    if (row.error) {
      skipped.push({ line: row.line, reason: row.error });
      continue;
    }
    try {
      const created = newPooledSubject({
        displayName: row.displayName,
        phone: row.phone || undefined,
        email: row.email || undefined,
        kind: row.kind,
        region: row.region || undefined,
        vertical: row.vertical,
        spendBand: row.spendBand,
        source: { system: 'csv' },
        actorId,
      });
      appendEvent('import', actorId, created.id, undefined, { name: row.displayName });
      imported += 1;
    } catch (e) {
      skipped.push({ line: row.line, reason: e instanceof SalesError ? e.message : String(e) });
    }
  }
  if (imported) emit();
  return { imported, skipped };
}

export function createProspect(
  input: {
    displayName: string;
    phone?: string;
    email?: string;
    kind?: SubjectKind;
    region?: string;
    vertical?: SalesVertical;
    spendBand?: SpendBand;
    uid?: number;
    oid?: number;
    notes?: string;
    alreadyOnPlatform?: boolean;
  },
  actorId: string,
): SalesSubject {
  const name = input.displayName.trim();
  if (!name) fail('validation', 'Name is required.');
  const phone = input.phone?.trim() || undefined;
  const email = input.email?.trim() || undefined;
  if (!phone && !email && input.uid == null && input.oid == null) {
    fail('validation', 'Phone, email, or a FeelyFeely id is required.');
  }
  const created = newPooledSubject({
    displayName: name,
    phone,
    email,
    kind: input.kind ?? 'person',
    region: input.region,
    vertical: input.vertical ?? 'homes',
    spendBand: input.spendBand ?? 'unknown',
    source: input.alreadyOnPlatform || input.uid != null || input.oid != null ? { system: 'platform' } : { system: 'manual' },
    uid: input.uid,
    oid: input.oid,
    actorId,
    notes: input.notes,
  });
  appendEvent('import', actorId, created.id, undefined, { name, source: created.source.system });
  emit();
  return created;
}

const STAGE_WEIGHT: Record<ClaimStatus, number> = {
  claimed: 1,
  in_pursuit: 0.75,
  committed: 0.45,
  won: 0,
  lost: 0,
  released: 0,
  expired: 0.9,
};

export function slaUrgency(claim: SalesClaim, at = nowMs()): number {
  const exp = new Date(claim.expiresAt).getTime();
  if (exp <= at) return 1;
  const claimed = new Date(claim.claimedAt).getTime();
  const span = exp - claimed;
  if (span <= 0) return 1;
  return clamp01(1 - (exp - at) / span);
}

export function nextBestScore(claim: SalesClaim, at = nowMs()): number {
  const subject = subjectById(claim.subjectId);
  const purpose = purposeById(claim.purposeId);
  if (!subject || !purpose) return 0;
  const status = displayStatus(claim, at);
  return slaUrgency(claim, at) * fitScore(subject, purpose.goal) * (STAGE_WEIGHT[status] ?? 0.5);
}

export function deskQueue(ownerId: string): SalesClaim[] {
  const at = nowMs();
  return claims
    .filter((c) => c.ownerId === ownerId && isLockingClaim(c, at))
    .sort((a, b) => nextBestScore(b, at) - nextBestScore(a, at));
}

function periodStart(period: PerformancePeriod, at: number): number {
  if (period === 'all') return 0;
  if (period === '7d') return at - 7 * 86400_000;
  if (period === '90d') return at - 90 * 86400_000;
  return at - 30 * 86400_000;
}

function emptyGoalPerf(): PerformanceSnapshot['byGoal'] {
  return {
    signup: { won: 0, lost: 0, wonGhs: 0 },
    supply: { won: 0, lost: 0, wonGhs: 0 },
    monetize: { won: 0, lost: 0, wonGhs: 0 },
  };
}

function emptyLoss(): PerformanceSnapshot['lossReasons'] {
  return {
    price: 0,
    no_inventory: 0,
    effort_too_high: 0,
    competitor: 0,
    unreachable: 0,
    not_a_fit: 0,
  };
}

function influencedSpend(subject: SalesSubject): number {
  return subject.platform?.lifetimePaidGhs ?? 0;
}

export function performanceSnapshot(period: PerformancePeriod, repId?: string): PerformanceSnapshot {
  const at = nowMs();
  const from = periodStart(period, at);
  const inPeriod = (iso: string) => {
    const t = new Date(iso).getTime();
    return t >= from && t <= at;
  };
  const owned = (c: SalesClaim) => (repId ? c.ownerId === repId : true);
  const byGoal = emptyGoalPerf();
  const lossReasons = emptyLoss();
  let claimedN = 0;
  let firstTouched = 0;
  let committed = 0;
  let won = 0;
  let lost = 0;
  let expired = 0;
  let slaHits = 0;
  let slaDenom = 0;
  let wonGhs = 0;
  let offerCostGhs = 0;
  let winDays = 0;
  let winN = 0;
  let pipelineGhs = 0;
  const touches = touchesForState();

  for (const c of claims) {
    if (!owned(c)) continue;
    const purpose = purposeById(c.purposeId);
    const value = purpose?.estimatedValueGhs ?? 0;
    const offer = offerById(c.offerId);

    if (isLockingClaim(c, at)) pipelineGhs += value;

    if (inPeriod(c.claimedAt)) {
      claimedN += 1;
      const firstTouch = touches
        .filter((t) => t.claimId === c.id)
        .sort((a, b) => a.at.localeCompare(b.at))[0];
      const slaMs = policy.firstTouchHours * 3600_000;
      const claimedMs = new Date(c.claimedAt).getTime();
      const windowEnd = claimedMs + slaMs;
      if (firstTouch) {
        firstTouched += 1;
        slaDenom += 1;
        if (new Date(firstTouch.at).getTime() <= windowEnd) slaHits += 1;
      } else if (at >= windowEnd) {
        slaDenom += 1;
      }
    }

    if (c.status === 'committed' && inPeriod(c.claimedAt)) committed += 1;

    if (c.status === 'won' && c.win && inPeriod(c.win.at)) {
      won += 1;
      wonGhs += value;
      byGoal[c.win.goal].won += 1;
      byGoal[c.win.goal].wonGhs += value;
      if (offer) offerCostGhs += offer.costGhs;
      const days = (new Date(c.win.at).getTime() - new Date(c.claimedAt).getTime()) / 86400_000;
      if (Number.isFinite(days)) {
        winDays += days;
        winN += 1;
      }
    }
    if (c.status === 'lost' && inPeriod(c.claimedAt)) {
      lost += 1;
      if (purpose) byGoal[purpose.goal].lost += 1;
      if (c.lossReason) lossReasons[c.lossReason] += 1;
    }
    if ((c.status === 'expired' || isDerivedExpired(c, at)) && inPeriod(c.claimedAt)) expired += 1;
  }

  const closed = won + lost;
  let cooldownFrozen = 0;
  let poolAvailable = 0;
  let researchCount = 0;
  let poolCount = 0;
  let userCount = 0;
  let customerCount = 0;
  let influencedSpendGhs = 0;
  for (const s of subjects) {
    if (s.acquisitionStage === 'research') {
      researchCount += 1;
      continue;
    }
    poolCount += 1;
    if (s.acquisitionStage === 'user') userCount += 1;
    if (s.acquisitionStage === 'customer') customerCount += 1;
    const av = availabilityOf(s, at);
    if (av === 'available') poolAvailable += 1;
    if (av === 'cooling' || av === 'do_not_contact') cooldownFrozen += 1;
    const ownerMatch = !repId || claims.some((c) => c.subjectId === s.id && c.ownerId === repId);
    if (ownerMatch) influencedSpendGhs += influencedSpend(s);
  }

  const relevantOutreach = outreaches.filter((o) => {
    if (!inPeriod(o.at)) return false;
    if (!repId) return true;
    return o.actorId === repId;
  });
  const outreachSent = relevantOutreach.filter((o) => o.status !== 'draft' && o.status !== 'blocked').length;
  const outreachReplied = relevantOutreach.filter((o) => o.status === 'replied' || o.feedback).length;
  const outreachOpened = relevantOutreach.filter(
    (o) => o.status === 'opened' || o.status === 'clicked' || o.status === 'replied' || o.trackedLinks.some((l) => l.events.some((e) => e.kind === 'open')),
  ).length;
  const outreachClicked = relevantOutreach.filter(
    (o) => o.status === 'clicked' || o.status === 'replied' || o.trackedLinks.some((l) => l.events.some((e) => e.kind === 'click')),
  ).length;
  const outreachBlocked = relevantOutreach.filter((o) => o.status === 'blocked').length;

  const quotaGhs = repId
    ? getRep(repId)?.quotaGhs ?? 0
    : SALES_REPS.filter((r) => r.role !== 'manager').reduce((s, r) => s + (r.quotaGhs ?? 0), 0);

  return {
    period,
    fromMs: from,
    toMs: at,
    claimed: claimedN,
    firstTouched,
    committed,
    won,
    lost,
    expired,
    pipelineGhs,
    wonGhs,
    offerCostGhs,
    winRate: closed ? won / closed : 0,
    slaHitRate: slaDenom ? slaHits / slaDenom : 0,
    avgDaysToWin: winN ? winDays / winN : null,
    quotaGhs,
    byGoal,
    lossReasons,
    cooldownFrozen,
    poolAvailable,
    researchCount,
    poolCount,
    userCount,
    customerCount,
    outreachSent,
    outreachReplied,
    outreachOpened,
    outreachClicked,
    outreachBlocked,
    influencedSpendGhs,
  };
}

export type SelfCheckRow = { name: string; ok: boolean; detail: string };

export function runPolicySelfCheck(): SelfCheckRow[] {
  const rows: SelfCheckRow[] = [];
  const check = (name: string, fn: () => void, expectCode?: string) => {
    try {
      fn();
      rows.push({
        name,
        ok: !expectCode,
        detail: expectCode ? 'Expected rejection, but the action succeeded.' : 'Passed.',
      });
    } catch (e) {
      const err = e instanceof SalesError ? e : null;
      if (expectCode) {
        const ok = err?.code === expectCode;
        rows.push({
          name,
          ok,
          detail: ok ? err!.message : `Wanted ${expectCode}, got ${err?.code ?? String(e)}`,
        });
      } else {
        rows.push({ name, ok: false, detail: err?.message ?? String(e) });
      }
    }
  };

  const free = subjects.find((s) => isInPool(s) && availabilityOf(s) === 'available' && !lockingClaimForSubject(s.id));
  check('claim without purpose', () => claimLead({ subjectId: free?.id ?? 'x', ownerId: 'rep_ama', purposeId: '' }), 'missing_purpose');

  const research = subjects.find((s) => s.acquisitionStage === 'research');
  if (research) {
    check(
      'claim research before pooling',
      () => claimLead({ subjectId: research.id, ownerId: 'rep_ama', purposeId: 'pur_signup' }),
      'not_pooled',
    );
  }

  const locked = claims.find((c) => isLockingClaim(c));
  if (locked) {
    check(
      'double-claim locked subject',
      () => claimLead({ subjectId: locked.subjectId, ownerId: 'rep_kojo', purposeId: 'pur_signup' }),
      'locked',
    );
  }

  const dnc = subjects.find((s) => s.doNotContact);
  if (dnc) {
    check('claim DNC', () => claimLead({ subjectId: dnc.id, ownerId: 'rep_ama', purposeId: 'pur_signup' }), 'do_not_contact');
  }

  const cooling = subjects.find((s) => isInPool(s) && availabilityOf(s) === 'cooling');
  if (cooling) {
    check('claim cooling', () => claimLead({ subjectId: cooling.id, ownerId: 'rep_ama', purposeId: 'pur_signup' }), 'cooling');
  }

  const dup = subjects.find((s) => s.id === 'sub_yaw_meqasa');
  if (dup) {
    check(
      'identity collision',
      () => claimLead({ subjectId: dup.id, ownerId: 'rep_kojo', purposeId: 'pur_homes' }),
      'identity_collision',
    );
  }

  const won = claims.find((c) => c.status === 'won' && c.ownerId === 'rep_ama');
  if (won) {
    check('advance by other owner', () => advanceClaim(won.id, 'pur_featured', 'rep_kojo'), 'not_owner');
  }

  const active = claims.find((c) => c.status === 'in_pursuit' && c.id !== 'clm_cap');
  if (active) {
    check('win without evidence', () => winClaim(active.id, active.ownerId, {}), 'evidence');
    check('lose without reason', () => loseClaim(active.id, active.ownerId, 'not_a_reason' as LossReason), 'loss_reason');
    check('reassign as non-manager', () => reassignClaim(active.id, 'rep_yaa', 'rep_kojo'), 'not_manager');
  }

  const capClaim = claims.find((c) => c.id === 'clm_cap');
  if (capClaim) {
    check(
      'whatsapp channel cap',
      () => logTouch({ claimId: capClaim.id, actorId: 'rep_kojo', channel: 'whatsapp', summary: 'cap test' }),
      'channel_cap',
    );
  }

  const missingConsent = subjects.find(
    (s) => isInPool(s) && !s.doNotContact && s.consent.whatsapp !== true,
  );
  if (missingConsent) {
    check(
      'missing consent blocks outreach',
      () =>
        logOffPlatform({
          subjectId: missingConsent.id,
          actorId: 'rep_ama',
          channel: 'whatsapp',
          body: 'consent check',
        }),
      'no_consent',
    );
  }

  check('happy-path helpers exist', () => {
    if (typeof fitScore !== 'function') throw new Error('missing fitScore');
  });

  return rows;
}

export function linkByToken(token: string): { outreach: SalesOutreach; dest: string } | undefined {
  for (const o of outreaches) {
    const link = o.trackedLinks.find((l) => l.token === token);
    if (link) return { outreach: o, dest: link.dest };
  }
  return undefined;
}

export { SALES_REPS };
export type { SalesVertical };
